import { gql, type TypedDocumentNode, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import type { TeamMembershipPageQuery, TeamMembershipPageQueryVariables } from 'types/graphql';

/** The Team page's membership read: only the rows behind the Clerk members on screen (P7-R35). */
export const TEAM_MEMBERSHIPS_QUERY: TypedDocumentNode<TeamMembershipPageQuery, TeamMembershipPageQueryVariables> = gql`
    query TeamMembershipPageQuery($input: StandardPageInput) {
      membershipPage(input: $input) {
        edges {
          node {
            id
            subsidiaryId
            clerkId
          }
        }
      }
    }
  `;

/**
 * The organization's membership rows for these Clerk organization-membership ids. The page is
 * exactly as large as the id list (Clerk loads ≤ 500), so nothing is truncated; the ids are sorted
 * so the same members in another order reuse the cached answer.
 */
export function useMemberships(clerkIds: readonly string[]) {
  const key = [...clerkIds].sort().join('\n');
  const variables = useMemo<TeamMembershipPageQueryVariables>(() => {
    const ids = key === '' ? [] : key.split('\n');
    return { input: { first: ids.length, gridFilters: [{ field: 'clerkId', where: [{ in: ids }] }] } };
  }, [key]);
  const { data, loading, error } = useQuery(TEAM_MEMBERSHIPS_QUERY, { variables, skip: key === '' });
  const memberships = useMemo(() => data?.membershipPage.edges?.flatMap((edge) => (edge ? [edge.node] : [])), [data]);

  return { memberships, loading, error };
}
